import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "react-toastify";

import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { useAlbumsControllerAlbumCreate } from "@/lib/lutely-api/gen/albums/albums";
import type { AlbumKind } from "@/lib/lutely-api/gen/dtos";
import { UNTITLED_RELEASE_TITLE } from "@/lib/utils";

/**
 * Shared "new release" action: creates an empty release for the artist
 * currently selected in Audiolab and lands on its page, where title and tracks
 * are edited in place — no naming or add-tracks modal in between. Every entry
 * point (Create menu, the sales empty state, the Get started checklist) goes
 * through this so they can't drift apart, and reading the creator here rather
 * than taking it as an argument is what keeps them on the same artist.
 */
export function useCreateRelease() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedCreator } = useAudiolab();
  const { mutateAsync: createAlbum, isPending: isCreatingRelease } =
    useAlbumsControllerAlbumCreate();
  const creatorId = selectedCreator?.creatorId;

  const createRelease = async (kind: AlbumKind) => {
    if (!creatorId || isCreatingRelease) return;
    try {
      const { albumId } = await createAlbum({
        data: { creatorId, title: UNTITLED_RELEASE_TITLE, kind },
      });
      queryClient.invalidateQueries({ queryKey: ["/albums"] });
      navigate({
        to: "/audiolab/releases/$ALBUM_ID",
        params: { ALBUM_ID: albumId },
      });
    } catch (err) {
      toast.error(getErrorMessage(err) || "Failed to create release");
    }
  };

  return { createRelease, isCreatingRelease, canCreate: !!creatorId };
}
